/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button as MuiButton, Grid, Stack, Typography, Tooltip, ToggleButton, ToggleButtonGroup } from "@mui/material";
import ViewListIcon from "@mui/icons-material/ViewList";
import MapIcon from "@mui/icons-material/Map";
import { useNodeDisplay } from "./hooks";
import { IServiceDeviceData } from "../../../store/api/devices/types";
import DeviceDrawer from "../../../components/Drawers/DeviceDrawer";
import CustomDataGrid from "../../../components/Shared/Table/CustomDataGrid";
import { GRID_CHECKBOX_SELECTION_COL_DEF, GridRowSelectionModel, GridSortModel } from "@mui/x-data-grid-pro";
import { useSelector } from "react-redux";
import { noOfSelectedRowSelector } from "../../../store/api/devices/selectors";
import { useStoreDispatch } from "../../../store/hooks";
import { setNoOfSelectedRow } from "../../../store/api/devices/reducers";
import AddRemoveSiteGroup from "../../../components/Nodes/SiteGroups/AddRemoveSiteGroup";

import { setBasicDialogOpenFor } from "../../../store/helpers/helperReducers";
import { DIALOGOPENFOR } from "../../../globals/globals";
import { basicDialogSelector } from "../../../store/helpers/helperSelectors";
import { nodeDisplayOptions } from "../../../components/Actions/ActionOptions";
import { prepareActionProps } from "../../../components/Actions/ActionHelper";
import { useGetDasFeatureFlagsQuery } from "../../../store/api/devices/deviceApiSlice";
import { isDasModel } from "../../Configuration/helpers";
import Actions from "../../../components/Actions/Actions";
import NodesFilter from "../../../components/Nodes/NodesFilter/NodesFilter";
import { useNodesFilter } from "../../../components/Nodes/NodesFilter/hooks";
import { getSortBy } from "../../../utils/helpers";
import { useLocation } from "react-router-dom";
import SmartMapView from "./SmartMapView";
import { Button } from "../../../components/ui/button";
import { Icon } from "../../../components/Shared/Icon";
import { Loader2 } from "lucide-react";
import ActiveFilterChips from "../../../components/Shared/Filter/ActiveFilterChips";
import { searchKeywordSelector } from "../../../store/search/selector";
import { resetKeyword } from "../../../store/search/reducer";
import { resetDeviceFilters } from "../../../store/filters/Nodes/reducer";

const NodeDisplay: React.FC = () => {
  const dispatch = useStoreDispatch();
  const location = useLocation();
  const [viewMode, setViewMode] = useState<'list' | 'map'>(() => {
    // Check if navigated from dashboard with showMap flag
    return location.state?.showMap ? 'map' : 'list';
  });

  const {
    permissions,
    data,
    isLoading,
    nodesColumns,
    handleRowClicked,
    selectedRowData,
    rowClicked,
    setRowClicked,
    exportHandler,
    exportStatus,
    setSortOrderToNode,
  } = useNodeDisplay(viewMode === 'map');

  const { filters: nodeFilters } = useNodesFilter();
  const searchKeyword = useSelector(searchKeywordSelector);

  const { deviceRowIds = [] } = useSelector(noOfSelectedRowSelector);
  const rowsData = data?.data || [];
  const noOfRows = data?.totalItems || 0;

  const selectedDeviceModelName = useMemo(() => {
    if (deviceRowIds.length === 0 || !data?.data) return "";
    const selected = data.data.find((item: any) => item?.objectKey === deviceRowIds[0]);
    return selected?.modelName || "";
  }, [deviceRowIds, data]);

  const { data: dasFeatureFlags } = useGetDasFeatureFlagsQuery(selectedDeviceModelName, {
    skip: !selectedDeviceModelName || !isDasModel(selectedDeviceModelName),
  });
  const inventoryDiscoveryEnabled = isDasModel(selectedDeviceModelName)
    ? (dasFeatureFlags?.inventoryDiscovery ?? true)
    : true;

  const handleClearSearch = useCallback(() => {
    dispatch(resetKeyword());
  }, [dispatch]);

  const handleClearAllFilters = useCallback(() => {
    dispatch(resetDeviceFilters());
    dispatch(resetKeyword());
  }, [dispatch]);

  const handleViewModeChange = (event: React.MouseEvent<HTMLElement>, newMode: 'list' | 'map') => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const actionProps = useMemo(
    () => deviceRowIds.length > 0 && prepareActionProps(deviceRowIds[0], "", data, nodeDisplayOptions, "nodeDisplay", false, permissions, inventoryDiscoveryEnabled),
    [data, deviceRowIds, permissions, inventoryDiscoveryEnabled]
  );

  const handleCloseButtonClicked = (value: boolean) => {
    setRowClicked(value);
  };

  const handleOnRowSelected = (rowIds: GridRowSelectionModel) => {
    dispatch(setNoOfSelectedRow({ deviceRowIds: rowIds }));
  };

  const basicDialogOpenFor = useSelector(basicDialogSelector);

  const addToSiteGroupHandler = () => {
    dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.SITEGROUPSTODEVICE));
  };

  const currentRoute = location.pathname;

  useEffect(() => {
    localStorage.setItem("previousRoute", currentRoute);
  }, [currentRoute]);

  useEffect(() => {
    dispatch(setNoOfSelectedRow({ deviceRowIds: [] }));
    return () => {
      dispatch(setNoOfSelectedRow({ deviceRowIds: [] }));
    };
  }, [dispatch]);


  return (
    <div>
      {basicDialogOpenFor === DIALOGOPENFOR.SITEGROUPSTODEVICE && <AddRemoveSiteGroup />}
      {rowClicked && selectedRowData && <DeviceDrawer data={selectedRowData} closedButtonClicked={handleCloseButtonClicked} />}
      <Grid item sx={{ mb: 1, ml: 0 }}>
        <NodesFilter>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            sx={{
              ml: 2,
              height: '31px',
              '& .MuiToggleButton-root': {
                color: 'var(--muted-foreground)',
                borderColor: 'var(--border)',
                padding: '4px 8px',
                '&.Mui-selected': {
                  color: 'var(--foreground)',
                  backgroundColor: 'var(--accent)',
                },
                '&:hover': {
                  backgroundColor: 'var(--accent)',
                },
              },
            }}
          >
            <ToggleButton value="list" aria-label="list view">
              <Tooltip title="List View">
                <ViewListIcon style={{ fontSize: "20px" }} />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="map" aria-label="map view">
              <Tooltip title="Map View">
                <MapIcon style={{ fontSize: "20px" }} />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </NodesFilter>
      </Grid>
      {viewMode === 'list' ? (
        <>
          {deviceRowIds.length > 0 ? (
            <Grid item md={12} sm={12} xs={12} sx={{ p: 0, marginTop: "12px" }}>
              <Stack spacing={2} direction="row" alignItems="center">
                <span className="pl-2 text-sm text-muted-foreground">{`${deviceRowIds.length} device${deviceRowIds.length > 1 ? 's' : ''} selected`}</span>
                {permissions?.hasOpCodeGroup("manage_topology") && (
                  <Button size={"sm"} variant="secondary" onClick={addToSiteGroupHandler}>
                    Device Group
                  </Button>
                )}
                {deviceRowIds.length === 1 && !!actionProps && (
                  <>
                    <Actions actionProps={actionProps} size="medium" tableId="deviceRow" />
                  </>
                )}
              </Stack>
            </Grid>
          ) : <div className="h-[34px]" />}
          <div className="flex justify-between items-end mb-1 mt-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">{`Showing ${noOfRows} results`}</span>
              <ActiveFilterChips
                filters={nodeFilters}
                searchKeyword={searchKeyword}
                onClearSearch={handleClearSearch}
                onClearAll={handleClearAllFilters}
                dispatch={dispatch}
              />
            </div>
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 shrink-0 relative -top-1"
              disabled={exportStatus.isLoading}
              onClick={exportHandler}
              aria-label="Export CSV"
            >
              {exportStatus.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon name="download" size={18} />}
            </Button>
          </div>
          <div>
            <CustomDataGrid
              tableId="nodeDisplay"
              rows={rowsData}
              columns={nodesColumns}
              checkboxSelection={true}
              rowCount={noOfRows}
              loading={isLoading}
              disableColumnMenu={true}
              disableRowSelectionOnClick={true}
              onRowSelectionModelChange={handleOnRowSelected}
              rowSelectionModel={deviceRowIds}
              getRowId={(row: IServiceDeviceData) => row.objectKey}
              onRowClick={handleRowClicked}
              pinnedColumns={{ left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, "geoRegionName", "name", "nodeType"] }}
              onSortModelChange={(params: GridSortModel) => setSortOrderToNode(getSortBy(params))}
              fillContainer
            />
          </div>
        </>
      ) : (
        <Grid item md={16} sm={16} sx={{ p: 7 }}>
          <SmartMapView />
        </Grid>
      )}
    </div>
  );
};

export default NodeDisplay;
